import {Button, Card, CardContent, Stack} from '@mui/material';
import {DataGrid, GridColDef} from '@mui/x-data-grid';
import {useDataGrid} from '@refinedev/mui';
import {DateTime} from 'luxon';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router';

import {fromPhpDate} from '@app/helpers/from-php-date';
import {DeleteButton} from '@components/buttons/delete';
import {EditButton} from '@components/buttons/edit';
import {SubscriptionStatus, subscriptionStatusOptions} from '@components/chip';
import {List} from '@components/crud/list';
import {Account} from '@pages/pilotage-rh/accounts/account-types';

const ListAccount = () => {
  const {t} = useTranslation();
  const {dataGridProps} = useDataGrid<Account>({
    syncWithLocation: true,
  });

  const bdeseUrl = import.meta.env.VITE_BDESE_URL || '';

  const exportList = () => {
    if (!dataGridProps.rows || dataGridProps.rows.length === 0) {
      return;
    }

    const csvRows = [];
    const headers = [
      'Nom du compte',
      'Utilisateur principal',
      "Email de l'utilisateur principal",
      'Offre PRH',
      'Expiration PRH',
      'Offre Egapro',
      'Expiration Egapro',
      'Offre Transparence',
      'Expiration Transparence',
      'Origine du compte',
      'Date de création',
    ];
    csvRows.push(headers.join(';'));

    dataGridProps.rows.forEach((row) => {
      const values = columns
        .filter((col) => col.field !== 'actions')
        .map((col) => {
          if (col.valueGetter) {
            return `"${col.valueGetter(col.field as never, row, col, undefined as any).replace(/"/g, '""')}"`;
          }

          if (col.field === 'subscriptions') {
            const getSubscriptionCsvFields = (sub: any): [string, string] => {
              const label = sub?.code
                ? subscriptionStatusOptions.find((opt) => opt.value === sub.code)?.label || 'Aucun'
                : 'Aucun';
              const expiresAt = sub?.expiresAt?.substring(0, 10) || 'n/a';
              return [label, expiresAt];
            };

            const prhSubscription = row[col.field].find((sub: any) => sub.service === 'PRH');
            const egaproSubscription = row[col.field].find((sub: any) => sub.service === 'EGAPRO');
            const transparenceSubscription = row[col.field].find((sub: any) => sub.service === 'TRS');

            return [
              ...getSubscriptionCsvFields(prhSubscription),
              ...getSubscriptionCsvFields(egaproSubscription),
              ...getSubscriptionCsvFields(transparenceSubscription),
            ].join(';');
          }

          return `"${row[col.field] !== undefined ? String(row[col.field]).replace(/"/g, '""') : ''}"`;
        });
      csvRows.push(values.join(';'));
    });

    const csvContent = 'data:text/csv;charset=utf-8,\uFEFF' + csvRows.join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'accounts.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const {rowCount, ...cleanDataGridProps} = dataGridProps;

  const columns = useMemo<GridColDef<Account>[]>(
    () => [
      {
        field: 'label',
        headerName: t('pages.pilotage-rh.account.fields.label'),
        flex: 1,
      },
      {
        field: 'fullName',
        headerName: t('pages.pilotage-rh.account.fields.name'),
        flex: 1,
        valueGetter: (value, row) => {
          return `${row.owner.firstName || ''} ${row.owner.lastName || ''}`;
        },
      },
      {
        field: 'email',
        headerName: t('pages.pilotage-rh.account.fields.email'),
        flex: 1,
        valueGetter: (value, row) => {
          return `${row.owner.email || ''}`;
        },
      },
      {
        field: 'subscriptions',
        headerName: t('pages.pilotage-rh.account.fields.subscriptions'),
        display: 'flex',
        renderCell: ({row}) => <SubscriptionStatus account={row} />,
        minWidth: 100,
        flex: 1,
      },
      {
        field: 'accountOrigin',
        headerName: t('pages.pilotage-rh.account.fields.account_origin'),
        display: 'flex',
        renderCell: ({row}) =>
          row.accountOrigin === 'BDESE' ? (
            <Link to={`${bdeseUrl}/admin/company/edit/${row.bdeseId}`} about="_blank">
              {row.accountOrigin}
            </Link>
          ) : row.accountOrigin ? (
            row.accountOrigin
          ) : (
            '-'
          ),
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'createdAt',
        headerName: t('pages.pilotage-rh.account.fields.createdAt'),
        flex: 1,
        renderCell: ({row}) => fromPhpDate(row.createdAt).toLocaleString(DateTime.DATETIME_SHORT),
      },
      {
        field: 'actions',
        headerName: t('actions.title'),
        display: 'flex',
        renderCell: function render({row}) {
          return (
            <Stack gap={1} direction="row">
              <EditButton
                hideText
                size="small"
                recordItemId={row.uuid}
                accessControl={{enabled: true, hideIfUnauthorized: true}}
              />
              <DeleteButton
                color="error"
                hideText
                size="small"
                recordItemId={row.uuid}
                confirmTitle={t('pages.global.user.delete.confirmTitle', {email: row.label})}
                successNotification={{
                  message: t('pages.pilotage-rh.account.notifications.delete.success'),
                  type: 'success',
                }}
                accessControl={{enabled: true, hideIfUnauthorized: true}}
              />
            </Stack>
          );
        },
        align: 'center',
        headerAlign: 'center',
      },
    ],
    [t],
  );

  return (
    <>
      <List>
        <Stack direction="row" justifyContent="flex-end" sx={{mb: 2}}>
          <Button variant="outlined" onClick={() => exportList()}>
            {t('buttons.export-clients')}
          </Button>
        </Stack>
        <Card>
          <CardContent sx={{padding: 0}}>
            <DataGrid
              {...cleanDataGridProps}
              columns={columns}
              paginationMode={'client'}
              sortingMode={'client'}
              filterMode={'client'}
              getRowId={(row) => row.uuid}
            />
          </CardContent>
        </Card>
      </List>
    </>
  );
};

export default ListAccount;
